/**
 * Vietnamese Language Profile
 *
 * SVO word order, prepositions, space-separated.
 * Vietnamese is an isolating (analytic) language with no inflection.
 * Uses Latin script with extensive diacritics for tone marking.
 */

import type { LanguageProfile } from './types';

export const vietnameseProfile: LanguageProfile = {
  code: 'vi',
  name: 'Vietnamese',
  nativeName: 'Tiếng Việt',
  direction: 'ltr',
  script: 'latin',
  wordOrder: 'SVO',
  markingStrategy: 'preposition',
  usesSpaces: true,
  // Vietnamese uses base/dictionary form for commands
  defaultVerbForm: 'base',
  verb: {
    position: 'start',
    subjectDrop: true,
  },
  references: {
    me: 'tôi', // "I/me"
    it: 'nó', // "it"
    you: 'bạn', // "you"
    result: 'kết quả',
    event: 'sự kiện',
    target: 'mục tiêu',
    body: 'body',
    document: 'tài liệu',
    window: 'cửa sổ',
  },
  possessive: {
    marker: 'của', // Vietnamese uses "của" for possession (của tôi = my)
    markerPosition: 'between',
    specialForms: {
      me: 'của tôi', // "my"
      it: 'của nó', // "its"
      you: 'của bạn', // "your"
    },
    keywords: {
      // Multi-word possessive phrases
      // Note: These may require tokenizer support for multi-word recognition
      'của tôi': 'me', // my
      // The vi dict's `my` is the two-word 'của tôi', which the dot-notation
      // transformer can't prefix onto `my.X` — those corpus heads stay literal
      // English `my.textContent`. Recognize the passthrough so the possessive
      // matcher can assemble the property-path (set-text/set-inner-html rows).
      my: 'me', // untranslated dot-notation passthrough
      'của bạn': 'you', // your (informal)
      'của anh': 'you', // your (male speaker, formal)
      'của chị': 'you', // your (female speaker, formal)
      'của nó': 'it', // its
    },
  },
  roleMarkers: {
    destination: { primary: 'vào', alternatives: ['cho', 'đến'], position: 'before' },
    source: { primary: 'từ', alternatives: ['khỏi'], position: 'before' },
    patient: { primary: '', position: 'before' },
    style: { primary: 'với', position: 'before' },
  },
  keywords: {
    // Class/Attribute operations
    toggle: { primary: 'chuyển đổi', alternatives: ['bật tắt', 'chuyển'], normalized: 'toggle' },
    add: { primary: 'thêm', alternatives: ['bổ sung'], normalized: 'add' },
    remove: { primary: 'xóa', alternatives: ['gỡ bỏ', 'loại bỏ', 'bỏ'], normalized: 'remove' },
    // Content operations
    put: { primary: 'đặt', alternatives: ['để', 'đưa'], normalized: 'put' },
    append: { primary: 'nối', normalized: 'append' },
    prepend: { primary: 'thêm vào đầu', normalized: 'prepend' },
    take: { primary: 'lấy', normalized: 'take' },
    make: { primary: 'tạo', normalized: 'make' },
    clone: { primary: 'nhân bản', normalized: 'clone' },
    swap: { primary: 'hoán đổi', normalized: 'swap' },
    morph: { primary: 'biến đổi', normalized: 'morph' },
    // Variable operations
    set: { primary: 'gán', alternatives: ['thiết lập'], normalized: 'set' },
    get: { primary: 'lấy giá trị', alternatives: ['nhận'], normalized: 'get' },
    increment: { primary: 'tăng', alternatives: ['tăng lên'], normalized: 'increment' },
    decrement: { primary: 'giảm', alternatives: ['giảm đi'], normalized: 'decrement' },
    log: { primary: 'in ra', normalized: 'log' },
    // Visibility
    show: { primary: 'hiển thị', alternatives: ['hiện'], normalized: 'show' },
    hide: { primary: 'ẩn', alternatives: ['che', 'giấu'], normalized: 'hide' },
    transition: { primary: 'chuyển tiếp', normalized: 'transition' },
    // Events
    on: { primary: 'khi', alternatives: ['trên'], normalized: 'on' },
    trigger: { primary: 'kích hoạt', normalized: 'trigger' },
    send: { primary: 'gửi', normalized: 'send' },
    // DOM focus
    focus: { primary: 'tập trung', normalized: 'focus' },
    blur: { primary: 'mất tập trung', normalized: 'blur' },
    // Phase 1 (v0.9.90): DOM / form state / debug
    empty: { primary: 'làm-rỗng', alternatives: ['trống'], normalized: 'empty' },
    open: { primary: 'mở', normalized: 'open' },
    close: { primary: 'đóng', normalized: 'close' },
    select: { primary: 'đánh-dấu', normalized: 'select' },
    clear: { primary: 'tẩy', normalized: 'clear' },
    reset: { primary: 'đặt-lại', normalized: 'reset' },
    breakpoint: { primary: 'điểm-dừng', normalized: 'breakpoint' },
    // Common event names (for event handler patterns)
    click: { primary: 'nhấp', alternatives: ['bấm'], normalized: 'click' },
    hover: { primary: 'di chuột', alternatives: ['rê chuột'], normalized: 'hover' },
    submit: { primary: 'nộp', alternatives: [], normalized: 'submit' },
    input: { primary: 'nhập', alternatives: ['nhập liệu'], normalized: 'input' },
    // Reactive `when <expr> changes` trigger word — synced VERBATIM from the i18n
    // dictionary (`changes`), which is what wrote every stored corpus row; the V1
    // vocab gate requires the two surfaces to agree. Native review pending — see
    // NATIVE_REVIEW_NEEDED.md § "Reactive `when … changes`".
    // Declared BEFORE `change` on purpose: the dictionary's changes-word is the
    // same surface as the `change` EVENT primary below, and the tokenizer's
    // keyword map is last-writer-wins per surface — so `thay đổi` keeps reading
    // as the event (`khi thay đổi …` = on change) while the reactive head still
    // matches it by SURFACE (block-parser locateReactiveWhenHead).
    changes: { primary: 'thay đổi', normalized: 'changes' },
    change: { primary: 'thay đổi', alternatives: ['đổi'], normalized: 'change' },
    // Navigation
    go: { primary: 'đi đến', alternatives: ['đi'], normalized: 'go' },
    scroll: { primary: 'cuộn', normalized: 'scroll' },
    push: { primary: 'đẩy', alternatives: ['push'], normalized: 'push' },
    replace: { primary: 'thay_thế', alternatives: ['thay_the'], normalized: 'replace' },
    process: { primary: 'xử_lý', alternatives: ['xu_ly'], normalized: 'process' },
    // Async
    wait: { primary: 'chờ', alternatives: ['đợi', 'chờ đợi'], normalized: 'wait' },
    fetch: { primary: 'tải', normalized: 'fetch' },
    settle: { primary: 'ổn định', normalized: 'settle' },
    // Control flow
    if: { primary: 'nếu', normalized: 'if' },
    // Spaced phrase (`trừ khi`) — what the i18n dict + transformer emit; the
    // BaseTokenizer's multi-word matcher catches it longest-first so the trailing
    // `khi` (=on/when) is not mistaken for a second event handler. The underscore
    // form is kept as an alternative for any caller that pre-joins the phrase.
    unless: { primary: 'trừ khi', alternatives: ['trừ_khi'], normalized: 'unless' },
    when: { primary: 'lúc', normalized: 'when' },
    where: { primary: 'ở_đâu', normalized: 'where' },
    else: { primary: 'không thì', alternatives: ['nếu không'], normalized: 'else' },
    repeat: { primary: 'lặp lại', normalized: 'repeat' },
    for: { primary: 'với mỗi', normalized: 'for' },
    while: { primary: 'trong khi', normalized: 'while' },
    // `repeat forever` loop keyword — corpus word recognized so loopType types
    // as `:literal` like EN (the repeat.loopType R1 residue; see spanish.ts).
    forever: { primary: 'mãi mãi', normalized: 'forever', alternatives: ['forever'] },
    continue: { primary: 'tiếp tục', normalized: 'continue' },
    halt: { primary: 'dừng', alternatives: ['dừng lại'], normalized: 'halt' },
    throw: { primary: 'ném', normalized: 'throw' },
    call: { primary: 'gọi', normalized: 'call' },
    return: { primary: 'trả về', normalized: 'return' },
    then: { primary: 'rồi', alternatives: ['sau đó', 'thì'], normalized: 'then' },
    and: { primary: 'và', normalized: 'and' },
    // Comparison operator (`target matches .x`). Without this keyword the surface
    // stays an identifier and leaks verbatim into the condition's raw expression,
    // which the core expression parser reads as English (modal-close-backdrop /
    // focus-trap drop their then-branch). Not an ActionType and has no command
    // schema, so no pattern is generated from it.
    matches: { primary: 'khớp', normalized: 'matches' },
    end: { primary: 'kết thúc', normalized: 'end' },
    // Advanced
    js: { primary: 'js', normalized: 'js' },
    async: { primary: 'bất đồng bộ', normalized: 'async' },
    tell: { primary: 'nói với', normalized: 'tell' },
    default: { primary: 'mặc định', normalized: 'default' },
    init: { primary: 'khởi tạo', normalized: 'init' },
    behavior: { primary: 'hành vi', normalized: 'behavior' },
    install: { primary: 'cài đặt', normalized: 'install' },
    measure: { primary: 'đo lường', normalized: 'measure' },
    beep: { primary: 'beep', normalized: 'beep' },
    ask: { primary: 'hỏi', normalized: 'ask' },
    answer: { primary: 'trả lời', normalized: 'answer' },
    break: { primary: 'ngắt', normalized: 'break' },
    copy: { primary: 'sao chép', normalized: 'copy' },
    exit: { primary: 'thoát', alternatives: ['thoát ra'], normalized: 'exit' },
    pick: { primary: 'chọn', normalized: 'pick' },
    render: { primary: 'kết xuất', normalized: 'render' },
    // Modifiers
    into: { primary: 'vào', alternatives: ['vào trong'], normalized: 'into' },
    before: { primary: 'trước', alternatives: ['trước khi'], normalized: 'before' },
    after: { primary: 'sau', alternatives: ['sau khi'], normalized: 'after' },
    // Event modifiers
    until: { primary: 'cho đến khi', normalized: 'until' },
    event: { primary: 'sự kiện', normalized: 'event' },
    from: { primary: 'từ', alternatives: ['khỏi'], normalized: 'from' },
    // Phase 8 (htmx v4 localized attributes): attribute-suffix keywords
    // used by the vocab generator — `sse-kết-nối`, `hx-trực-tiếp`, etc.
    // Vietnamese multi-word forms are hyphenated for single-token HTML
    // attribute suffixes (diacritics preserved).
    // `nối` is reserved as `append` primary — connect uses the compound form only.
    connect: { primary: 'kết-nối', alternatives: ['kết-nối-tới'], normalized: 'connect' },
    stream: { primary: 'truyền-phát', alternatives: ['phát', 'luồng'], normalized: 'stream' },
    live: { primary: 'trực-tiếp', alternatives: ['phát-trực-tiếp', 'live'], normalized: 'live' },
    socket: { primary: 'socket', alternatives: ['ổ-cắm', 'websocket'], normalized: 'socket' },
    // Reactive / realtime commands
    bind: { primary: 'ràng buộc', alternatives: ['liên kết', 'bind'], normalized: 'bind' },
    intercept: {
      primary: 'chặn',
      alternatives: ['chặn bắt', 'intercept'],
      normalized: 'intercept',
    },
    worker: { primary: 'worker', alternatives: ['công nhân'], normalized: 'worker' },
    eventsource: {
      primary: 'eventsource',
      alternatives: ['nguồn sự kiện'],
      normalized: 'eventsource',
    },
  },
  eventHandler: {
    keyword: { primary: 'khi', alternatives: ['trên'], normalized: 'on' },
    sourceMarker: { primary: 'trên', alternatives: ['tại'], position: 'before' },
    // Event marker: khi (when), used in SVO pattern
    // Pattern: khi [event] [verb] [patient] vào [destination?]
    // Example: khi nhấp chuyển đổi .active vào #button
    eventMarker: { primary: 'khi', alternatives: ['lúc'], position: 'before' },
    temporalMarkers: ['khi', 'lúc'], // temporal conjunctions (when)
  },
};
